/**
 * Slide-over de cadastro/edição de suprimento.
 *
 * Regras de tela (dono, 2026-09-10):
 * - dinheiro com máscara R$ (`InputDinheiro`, convenção única do ADR-0059);
 * - medidas em mm são inteiras (1850, nunca 1850,0);
 * - largura/comprimento/espessura/veio aparecem UMA vez: são colunas do
 *   Produto. O bloco "Dados do tipo" (catálogo do backend) só traz o que
 *   não tem coluna (cor, refilo, metros por rolo…).
 */
import React, { useEffect } from "react";
import Checkbox from "../../../components/Checkbox";

import InputDinheiro from "../../../components/InputDinheiro";
import SlideOverPanel from "../../../components/SlideOverPanel";
import { CATEGORIAS, UNIDADES } from "../helpers";
import CampoTextura from "./CampoTextura";
import { areaDaChapaM2 } from "./derivados";
import { tipoAceitaTextura } from "./textura";
import {
  Field, Form, FormGrid, InlineHint, Input, PrimaryButton,
  SecondaryButton, Select, SubText, Textarea,
} from "../styles";

/** Liga os botões do rodapé fixo do painel ao <form> (atributo `form`). */
export const FORM_ID = "form-produto";

/** Título de grupo do formulário: caixa alta pequena com linha acima. */
function Grupo({ children }) {
  return (
    <SubText as="div" style={{ marginTop: 6, paddingTop: 10, borderTop: "1px solid var(--divider)", fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase" }}>
      {children}
    </SubText>
  );
}

/** "1850.0" -> "1850" para o input; vazio continua vazio. */
export function inteiroTexto(v) {
  if (v === null || v === undefined || v === "") return "";
  const n = Number(v);
  return Number.isFinite(n) ? String(Math.round(n)) : "";
}

export default function FormularioProduto({
  editando, setEditando, tiposCatalogo, tipoAtual, onSalvar, onCancelar,
  // Reuso pela fila "Materiais a cadastrar" (ADR-0068): mesmo formulario,
  // titulo proprio e um aviso acima dos campos.
  titulo = null, aviso = null,
}) {
  const set = (campo) => (e) => setEditando((p) => ({ ...p, [campo]: e.target.value }));
  // Chapa inteira com as medidas: o m² por unidade é a área delas — mantido
  // no formulário para o que vai ao servidor bater com o que a tela mostra.
  const areaCalculada = editando?.unidade === "CH"
    ? areaDaChapaM2(editando?.largura_mm, editando?.comprimento_mm)
    : null;
  useEffect(() => {
    if (areaCalculada === null) return;
    const texto = areaCalculada.toFixed(4);
    setEditando((p) => (p && String(p.m2_por_unidade ?? "") !== texto ? { ...p, m2_por_unidade: texto } : p));
  }, [areaCalculada, setEditando]);
  const setInteiro = (campo) => (e) => {
    // Só dígitos: o campo é inteiro por definição de produto.
    const so = e.target.value.replace(/\D/g, "");
    setEditando((p) => ({ ...p, [campo]: so }));
  };

  return (
    <SlideOverPanel
      open={Boolean(editando)}
      onClose={onCancelar}
      title={titulo || (editando?.id ? "Editar suprimento" : "Novo suprimento")}
      width="min(90vw, 480px)"
      // Auditoria mobile 2026-09-24 (S3): o rodapé vai na área fixa do
      // painel, sempre visível — no celular o "Criar" só aparecia depois de
      // rolar 962px de formulário. Os botões ficam fora do <form> e se ligam
      // a ele pelo atributo `form`.
      footer={editando && (
        <>
          <SecondaryButton type="button" onClick={onCancelar}>Cancelar</SecondaryButton>
          <PrimaryButton type="submit" form={FORM_ID}>{editando.id ? "Salvar" : "Criar"}</PrimaryButton>
        </>
      )}
    >
      {editando && (
        <Form id={FORM_ID} onSubmit={onSalvar}>
          {aviso}
          {/* Grupos nomeados (auditoria mobile 2026-09-24): doze campos numa
              coluna sem hierarquia. A ordem dos campos não mudou. */}
          <Grupo>Identificação</Grupo>
          {/* O tipo é a PRIMEIRA pergunta: é ele que decide o resto do
              formulário. Sem isso, chapa e cola preenchiam os mesmos
              campos e nenhum dos dois tinha onde pôr o que importa. */}
          <Field>
            Tipo de suprimento *
            <Select
              value={editando.tipo_suprimento || ""}
              onChange={(e) => {
                const codigo = e.target.value;
                const t = tiposCatalogo.find((x) => x.codigo === codigo);
                setEditando((p) => ({
                  ...p,
                  tipo_suprimento: codigo,
                  // Unidade sugerida pelo tipo, e atributos zerados: os
                  // campos do tipo anterior não fazem sentido no novo.
                  unidade: t?.unidade_sugerida || p.unidade,
                  categoria: t?.categoria || p.categoria,
                  atributos: {},
                }));
              }}
            >
              <option value="">— escolha o tipo —</option>
              {[...new Set(tiposCatalogo.map((t) => t.familia))].map((fam) => (
                <optgroup key={fam} label={fam}>
                  {tiposCatalogo
                    .filter((t) => t.familia === fam)
                    .map((t) => (
                      <option key={t.codigo} value={t.codigo}>{t.label}</option>
                    ))}
                </optgroup>
              ))}
            </Select>
            {tipoAtual ? (
              <SubText>
                Categoria: {tipoAtual.categoria} · unidade sugerida:{" "}
                {tipoAtual.unidade_sugerida}
              </SubText>
            ) : null}
          </Field>
          <FormGrid>
            <Field>
              Código *
              <Input
                type="text"
                value={editando.codigo}
                onChange={set("codigo")}
                maxLength={40}
                required
                disabled={Boolean(editando.id)}
                autoFocus={!editando.id}
              />
            </Field>
            <Field>
              Unidade *
              <Select value={editando.unidade} onChange={set("unidade")}>
                {UNIDADES.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
              </Select>
            </Field>
          </FormGrid>
          <Field>
            Nome *
            <Input type="text" value={editando.nome} onChange={set("nome")} maxLength={200} required />
          </Field>
          <Field>
            Descrição
            <Textarea value={editando.descricao} onChange={set("descricao")} rows={2} />
          </Field>
          <Grupo>Estoque</Grupo>
          <FormGrid>
            <Field>
              Categoria
              <Select value={editando.categoria} onChange={set("categoria")} disabled={Boolean(tipoAtual?.categoria)}>
                {CATEGORIAS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </Select>
              {tipoAtual?.categoria ? <InlineHint>Definida pelo tipo.</InlineHint> : null}
            </Field>
            <Field>
              Estoque mínimo
              <Input
                type="number"
                step="0.001"
                min="0"
                value={editando.estoque_minimo}
                onChange={set("estoque_minimo")}
              />
              <InlineHint>Alerta quando saldo cai abaixo.</InlineHint>
            </Field>
          </FormGrid>

          <Grupo>Fornecimento</Grupo>
          <FormGrid>
            <Field>
              Fabricante
              <Input
                type="text"
                value={editando.fabricante ?? ""}
                onChange={set("fabricante")}
                maxLength={120}
                placeholder="Ex.: Duratex, Arauco"
              />
            </Field>
            <Field>
              Referência do fabricante
              <Input
                type="text"
                value={editando.referencia_fabricante ?? ""}
                onChange={set("referencia_fabricante")}
                maxLength={80}
              />
            </Field>
          </FormGrid>
          <Field>
            NCM
            <Input
              type="text"
              value={editando.ncm ?? ""}
              onChange={set("ncm")}
              maxLength={10}
              placeholder="4410.11.10"
              style={{ maxWidth: 180 }}
            />
          </Field>

          {/* Geometria — colunas do Produto, perguntadas UMA vez. Inteiras. */}
          {editando.categoria === "CHAPA" && (
            <>
              <Grupo>Chapa</Grupo>
              <FormGrid>
                <Field>
                  Largura (mm)
                  <Input type="text" inputMode="numeric" value={editando.largura_mm ?? ""} onChange={setInteiro("largura_mm")} placeholder="Ex.: 1850" />
                </Field>
                <Field>
                  Comprimento (mm)
                  <Input type="text" inputMode="numeric" value={editando.comprimento_mm ?? ""} onChange={setInteiro("comprimento_mm")} placeholder="Ex.: 2750" />
                </Field>
              </FormGrid>
              <FormGrid>
                <Field>
                  Espessura (mm)
                  <Input type="text" inputMode="numeric" value={editando.espessura_mm ?? ""} onChange={setInteiro("espessura_mm")} placeholder="Ex.: 18" />
                </Field>
                <Field>
                  Tem veio (sentido obrigatório)?
                  <Select
                    value={editando.tem_veio === true ? "true" : editando.tem_veio === false ? "false" : ""}
                    onChange={(e) => setEditando((p) => ({
                      ...p,
                      tem_veio: e.target.value === "" ? null : e.target.value === "true",
                    }))}
                  >
                    <option value="">Não informado</option>
                    <option value="true">Sim</option>
                    <option value="false">Não</option>
                  </Select>
                </Field>
              </FormGrid>
              <InlineHint>
                Necessário para <strong>contar chapas no orçamento por m²</strong>{" "}
                (nível M2 do motor de preço).
                {(() => {
                  const l = Number(editando.largura_mm);
                  const c = Number(editando.comprimento_mm);
                  if (!(l > 30) || !(c > 30)) return null;
                  const bruta = (l * c) / 1e6;
                  // Refilo por lado: o do cadastro (atributos.refilo_mm) ou o
                  // padrão de 10 mm (Plano de Suprimentos D13).
                  const refilo = Number((editando.atributos || {}).refilo_mm) || 10;
                  const util = ((l - 2 * refilo) * (c - 2 * refilo)) / 1e6;
                  return (
                    <>
                      {" "}m² bruta (custo): <strong>{bruta.toFixed(4)}</strong>{" "}
                      · m² útil (contagem, refilo {refilo} mm):{" "}
                      <strong>{util.toFixed(4)}</strong>
                    </>
                  );
                })()}
              </InlineHint>
            </>
          )}

          {/* Campos do TIPO. Renderizados a partir do catálogo do
              backend: cada tipo declara os seus, e só eles aparecem — e
              só o que NÃO tem coluna própria (cor, refilo, metros/rolo…). */}
          {tipoAtual && tipoAtual.campos.length > 0 ? (
            <>
              <Grupo>Dados de {tipoAtual.label.toLowerCase()}</Grupo>
              <FormGrid>
                {tipoAtual.campos.map((campo) => {
                  const valor = (editando.atributos || {})[campo.chave];
                  const setAttr = (v) =>
                    setEditando((p) => ({
                      ...p,
                      atributos: { ...(p.atributos || {}), [campo.chave]: v },
                    }));
                  if (campo.tipo === "bool") {
                    return (
                      <Field key={campo.chave}>
                        <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
                          <Checkbox checked={Boolean(valor)} onChange={(e) => setAttr(e.target.checked)} />
                          {campo.label}
                        </label>
                      </Field>
                    );
                  }
                  if (campo.tipo === "cor") {
                    // ADR-0070: amostra de cor (#RRGGBB). Vazio = sem amostra.
                    return (
                      <Field key={campo.chave}>
                        {campo.label}
                        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                          <input
                            type="color"
                            value={/^#[0-9a-f]{6}$/i.test(valor || "") ? valor : "#cccccc"}
                            onChange={(e) => setAttr(e.target.value.toUpperCase())}
                            aria-label={campo.label}
                            style={{ width: 44, height: 32, padding: 2, border: "1px solid var(--border)", borderRadius: 8, background: "var(--surface)", cursor: "pointer" }}
                          />
                          <Input value={valor || ""} placeholder="#RRGGBB" maxLength={7} onChange={(e) => setAttr(e.target.value)} style={{ flex: 1 }} />
                          {valor ? <button type="button" className="btn btn--secondary btn--sm" onClick={() => setAttr("")}>Limpar</button> : null}
                        </div>
                      </Field>
                    );
                  }
                  if (campo.tipo === "escolha") {
                    return (
                      <Field key={campo.chave}>
                        {campo.label}
                        <Select value={valor || ""} onChange={(e) => setAttr(e.target.value)}>
                          <option value="">—</option>
                          {(campo.opcoes || []).map((o) => <option key={o} value={o}>{o}</option>)}
                        </Select>
                      </Field>
                    );
                  }
                  return (
                    <Field key={campo.chave}>
                      {campo.label}
                      <Input
                        type={campo.tipo === "numero" ? "number" : "text"}
                        step={campo.tipo === "numero" ? "any" : undefined}
                        value={valor ?? ""}
                        onChange={(e) => setAttr(e.target.value)}
                      />
                    </Field>
                  );
                })}
                {tipoAceitaTextura(tipoAtual) ? <CampoTextura editando={editando} setEditando={setEditando} /> : null}
              </FormGrid>
            </>
          ) : null}

          <Grupo>Custo e situação</Grupo>
          <FormGrid>
            <Field>
              Custo médio
              <InputDinheiro
                aria-label="Custo médio"
                value={editando.custo_medio}
                onChange={(n) => setEditando((p) => ({ ...p, custo_medio: n }))}
              />
              <InlineHint>
                {editando.id
                  ? "Correção manual (auditada). Entradas via OC recalculam por média ponderada."
                  : "Em entradas via OC, será recalculado por média ponderada."}
              </InlineHint>
            </Field>
            <Field>
              Situação
              <Select
                value={editando.ativo === false ? "false" : "true"}
                onChange={(e) => setEditando((p) => ({ ...p, ativo: e.target.value === "true" }))}
              >
                <option value="true">Ativo</option>
                <option value="false">Inativo</option>
              </Select>
            </Field>
            <Field>
              Compra
              <Select
                value={editando.compravel === false ? "false" : "true"}
                onChange={(e) => setEditando((p) => ({ ...p, compravel: e.target.value === "true" }))}
              >
                <option value="true">Compra-se do fornecedor</option>
                <option value="false">Não se compra (retalho da prateleira)</option>
              </Select>
              <InlineHint>Material que não se compra nunca entra na requisição do PharuCut, mesmo que o plano o consuma.</InlineHint>
            </Field>
          </FormGrid>
          {editando.unidade === "CH" && (
            <Field>
              m² por unidade (área da chapa)
              {areaCalculada !== null ? (
                <>
                  <Input type="text" value={areaCalculada.toFixed(4)} readOnly aria-readonly="true"
                    aria-label="m² por unidade, calculado das medidas" style={{ opacity: 0.85, cursor: "default" }} />
                  <InlineHint>
                    Calculado das medidas da chapa: {inteiroTexto(editando.largura_mm)} × {inteiroTexto(editando.comprimento_mm)} mm.
                    Para mudar, altere a largura ou o comprimento.
                  </InlineHint>
                </>
              ) : (
                <>
                  <Input
                    type="number"
                    step="0.0001"
                    min="0"
                    value={editando.m2_por_unidade ?? ""}
                    onChange={set("m2_por_unidade")}
                    placeholder="Ex.: 5.0875"
                  />
                  <InlineHint>
                    <strong>Obrigatório para produto em CH</strong> (chapa inteira)
                    com custo {">"} 0 — o BOM converte R$/chapa em R$/m².
                    {editando.categoria === "CHAPA"
                      ? " Preencha largura e comprimento acima e ele é calculado sozinho."
                      : " Informe a área de uma chapa (ex.: 1850×2750 mm = 5.0875 m²)."}
                  </InlineHint>
                </>
              )}
            </Field>
          )}
        </Form>
      )}
    </SlideOverPanel>
  );
}
